import { useEffect, useState } from "react";
import { DueDateBasis } from "../../../../../dtos/DueDateBasis";
import { GrantDto } from "../../../../../dtos/GrantDto";
import { Modules } from "../../../../../dtos/Modules";
import { PermissionType } from "../../../../../dtos/PermissionType";
import { PhaseDto } from "../../../../../dtos/PhaseDto";
import { restorePhaseDate, updatePhaseDates } from "../../../../../api/v2/PhasesApi";
import { useAppContext } from "../../../AppContext";
import { checkPhasePermission, getPhaseStatusInfo, taskHeaderStatus } from "../../../Utils";
import { downloadPhaseFiles, showsDownloadAll } from "../downloadPhaseFiles";

export interface IPhaseHeaderProps {
    phase: PhaseDto;
    grantData?: GrantDto | null;
    module?: Modules;
    // Mirrors the legacy header's Expand/Contract state so the column widths line up.
    showStaticEntries?: boolean;
    // True when this phase is the one currently open in the tasklist, so the row can indicate it.
    selected?: boolean;
    // Called after a date edit so other views can refresh.
    onChanged?: () => void;
    // Clicking the phase name opens the tasklist summary. Omitted = the name isn't clickable.
    onShowSummary?: () => void;
    // Called after a new cycle is started from the header icon. Omitted = no icon.
    onCycleStarted?: () => void | Promise<void>;
    // Whether the phase's tasklist is open below the header; the status dot toggles it.
    expanded?: boolean;
    onToggleExpanded?: () => void;
}

const toDate = (d?: Date | null) => (d ? new Date(d) : undefined);

export function usePhaseHeaderController({ phase, grantData, module, showStaticEntries = false, selected = false, onChanged, onShowSummary, onCycleStarted, expanded = false, onToggleExpanded }: IPhaseHeaderProps) {
    const app = useAppContext();
    const [submission, setSubmission] = useState<Date | null>(phase.submissionDate ?? null);
    const [completion, setCompletion] = useState<Date | null>(phase.completionDate ?? null);
    const [submissionOverridden, setSubmissionOverridden] = useState(phase.submissionOverridden);
    const [completionOverridden, setCompletionOverridden] = useState(phase.completionOverridden);

    // Resync when the phase reloads with new dates (derivation ran, or a different phase opens).
    useEffect(() => {
        setSubmission(phase.submissionDate ?? null);
        setCompletion(phase.completionDate ?? null);
        setSubmissionOverridden(phase.submissionOverridden);
        setCompletionOverridden(phase.completionOverridden);
    }, [phase.id, phase.submissionDate, phase.completionDate, phase.submissionOverridden, phase.completionOverridden]);

    // Which columns this phase shows comes from its template settings (ticket 1040), not from a
    // phase-number map in the module. Absent settings mean show everything, which is what every
    // phase outside legacy DC wants.
    const display = phase.defaultPhase?.settings?.display;
    const showProgress = display?.showProjectedDates ?? true;
    const showSubmission = display?.showStartDate ?? true;
    const showCompletion = display?.showCompletionDate ?? true;
    const showComments = display?.showComments ?? true;

    // NoA + Months is the task's projected completion counted from the NoA (the template's end
    // offset), not its own length, which is what phase.offset holds. Only NoA-based templates
    // measure from the NoA.
    const settings = phase.defaultPhase?.settings;
    const noaMonths = settings?.dueDateBasis === DueDateBasis.NoaDate ? settings.offsetEnd?.months ?? null : null;

    // Legacy parity: the row shows the numbered name ("01 Schematic Design") - the same shape as the
    // legacy header's FullPhase label.
    const number = phase.defaultPhase?.number;
    const displayName = number != null ? `${String(number).padStart(2, "0")} ${phase.name}` : phase.name;

    const canEditDates = checkPhasePermission(phase, app, PermissionType.EditDates, true, grantData?.id);

    // Same View check the tasklist name used before it moved here, so the click stays permission-gated.
    const canViewSummary = !!onShowSummary
        && checkPhasePermission(phase, app, PermissionType.View, true, grantData?.id);

    const showSummary = () => {
        if (!canViewSummary) return;
        onShowSummary?.();
    };

    const applyResult = (data: PhaseDto) => {
        setSubmission(data.submissionDate ?? null);
        setCompletion(data.completionDate ?? null);
        setSubmissionOverridden(data.submissionOverridden);
        setCompletionOverridden(data.completionOverridden);
    };

    // Second guard: a save that would not move the date is not a save. Keeps an echoed value from
    // becoming a write - and an override flag - even if a stray event reaches here.
    const sameDay = (a: Date | null, b: Date | null) =>
        (a ? new Date(a).setHours(0, 0, 0, 0) : null) === (b ? new Date(b).setHours(0, 0, 0, 0) : null);

    const changeDate = async (field: "submission" | "completion", value: Date | null) => {
        if (sameDay(value, field === "submission" ? submission : completion)) return;

        if (field === "submission") { setSubmission(value); setSubmissionOverridden(!!value); }
        else { setCompletion(value); setCompletionOverridden(!!value); }

        // The endpoint takes both dates; send the current other one so only the changed field moves.
        const sub = field === "submission" ? value : submission;
        const comp = field === "completion" ? value : completion;
        const result = await updatePhaseDates(phase.id, sub, comp);
        if (result?.error) {
            app.errorToast(result.error.message);
            return;
        }
        if (result?.data) applyResult(result.data);
        onChanged?.();
    };

    const restoreDate = async (field: "submission" | "completion") => {
        const result = await restorePhaseDate(phase.id, field);
        if (result?.error) {
            app.errorToast(result.error.message);
            return;
        }
        if (result?.data) applyResult(result.data);
        onChanged?.();
    };

    const onDownloadAll = () => downloadPhaseFiles(app, phase.id, module ?? Modules.DC);
    const showDownloadAll = showsDownloadAll(phase);

    // The task status, drawn before the name the way the tasklist toolbar draws it elsewhere.
    const statusInfo = getPhaseStatusInfo(taskHeaderStatus(phase), phase.dueDate, phase.defaultPhase?.settings?.workflowType);

    // Legacy min/max: Start can't precede the projected start or follow Completion; Completion can't
    // precede Start (or the projected start when Start is unset).
    const submissionMin = toDate(phase.startDate);
    const submissionMax = completion ?? undefined;
    const completionMin = submission ?? toDate(phase.startDate);

    return {
        phase,
        displayName,
        grantData,
        module: module ?? Modules.DC,
        showStaticEntries,
        showProgress,
        showSubmission,
        showCompletion,
        showComments,
        noaMonths,
        selected,
        submission,
        completion,
        submissionOverridden,
        completionOverridden,
        canEditDates,
        canViewSummary,
        onCycleStarted,
        expanded,
        onToggleExpanded,
        showSummary,
        submissionMin,
        submissionMax,
        completionMin,
        changeDate,
        restoreDate,
        onDownloadAll,
        showDownloadAll,
        statusInfo,
    };
}
